<script setup lang="ts">
import { until, watchOnce } from '@vueuse/core';
import type DocumentCpf from './DocumentCpf.vue';
import type { UserDocument } from '@/types/UserData';
import { useUserJourney } from '~/composables/apostatudo/use-user-journey';
import {
  getBankAccountTypeLabel,
  joinDigit,
} from '@/utils/bank-account-labels';

type Props = {
  userHasDocument: boolean;
  userId: number;
  userCurrency: string;
  userLanguage: string;
  userCountry: string;
  minDepositAmount: number;
  maxDepositAmount: number;
  activeFirstDepositMessage?: boolean;
  isResultScreen?: boolean;
  selectedDepositMethod: PaymentsMethodsType;
  firstDepositMessage?: string;
  utmCampaign: string;
  utmSource: string;
  utmMedium: string;
};

const {
  userHasDocument,
  userId,
  userCurrency,
  userLanguage,
  userCountry,
  minDepositAmount,
  maxDepositAmount,
  activeFirstDepositMessage = false,
  isResultScreen = false,
  selectedDepositMethod,
  firstDepositMessage,
  utmCampaign,
  utmSource,
  utmMedium,
} = defineProps<Props>();

type Emits = {
  'change-screen': [DepositScreenType];
};

const emit = defineEmits<Emits>();

const { $metrics, $containment } = useNuxtApp();
const { t } = useTexts();
const { ApiService } = useApiService();
const { updateUserDocumentCPF, updateUserDocumentCURP } = useUserDataStore();
const { fetchUserProfile, hasBankAccount, userBankAccounts } = useUserData();
const { openToaster, openChallenge, closeModal } = useLayoutStore();
const { modalType, isModalOpen } = storeToRefs(useLayoutStore());
const { autoSubmitDeposit, autoSubmitDepositAmount } =
  storeToRefs(useWalletStore());

const { clearAutoSubmitDeposit, selectDepositMethod } = useWalletStore();
const { showMoneyFormat, updateWallet } = useWallet();
const { gaClientId } = useParameters();
const { isLoading } = storeToRefs(usePaymentsStore());
const { defaultSelectedDepositMethod } = usePayments();
const { makeDepositEvent } = useUserJourney();

const paymentDefaultRes = ref<DefaultDepositInfoResponse>({
  qr_code: '',
  br_code: '',
  clabe: '',
  payment_link: '',
  barcode: '',
  barcode_number: '',
  astropay_response: null,
  checkout_page: null,
  value: 0,
  amount: 0,
});

const routePaymentValue = useRoute().query.value;

function setRouteValuesToPayAmount() {
  if (!routePaymentValue || isNaN(Number(routePaymentValue))) return;
  depositValues.value.amount = String(routePaymentValue);
}

onMounted(async () => {
  setRouteValuesToPayAmount();

  // Auto-submit direto — aguarda o componente estar totalmente montado
  if (autoSubmitDeposit.value && autoSubmitDepositAmount.value) {
    const amount = autoSubmitDepositAmount.value;

    clearAutoSubmitDeposit();

    depositValues.value.amount = amount;

    // Aguarda os métodos de pagamento carregarem antes de submeter; senão o POST
    // sai com o método de fallback ('pix'), que pode estar inativo no ambiente.
    if (isLoading.value) {
      await until(isLoading).toBe(false, { timeout: 10000 });
    }

    // Garante que o método submetido é o primeiro disponível (ex.: triopay),
    // não o 'pix' selecionado antes do carregamento dos métodos.
    selectDepositMethod(defaultSelectedDepositMethod.value);

    await nextTick();

    // Vai direto pro submit, sem passar pelo guard de consentimento do
    // checkbox (ver initSubmitDeposit): esse fluxo não renderiza o checkbox
    // pro jogador marcar, então o guard sempre bloquearia silenciosamente.
    await submitDeposit();
  }
});

const depositRow = ref<HTMLDivElement | null>(null);

const isDepositLoading = ref<boolean>(false);

const showLoading = computed(() => {
  return isDepositLoading.value || isLoading.value;
});

const depositMethodName = computed<string>(() => {
  if (isLoading.value) {
    return 'Pix';
  }
  if (selectedDepositMethod === 'credit-card') {
    return t('deposit.general.credit-card');
  }

  return selectedDepositMethod;
});

const depositValues = ref({
  amount: '',
  doc: {
    docnumber: '',
    docnumber_valid: false,
    full_name: '',
    birth_date: '',
  },
});

const saveBankAccount = ref(false);

// Continua obrigatório, mas não bloqueia mais o botão (isSubmitButtonEnabled)
// — clicar sem marcar mostra o erro em vez de deixar o botão desabilitado
// sem explicação. Limpa sozinho assim que o jogador marca a caixa.
const showBankAccountConsentError = ref(false);

// `:key` na linha do checkbox (ver template): incrementar troca o elemento
// inteiro (destrói/recria), senão a @keyframes de chacoalhar só toca na
// primeira vez — reaplicar a MESMA classe (nextTick tirando/recolocando)
// não é suficiente pro navegador reiniciar a animação de forma confiável.
const checkboxShakeKey = ref(0);

watch(saveBankAccount, (checked) => {
  if (checked) {
    showBankAccountConsentError.value = false;
  }
});

// Trocar de método de pagamento esconde/reexibe o checkbox (v-if em
// isBankAccountDepositMethod); sem isso, um erro de uma tentativa anterior
// reaparecia vermelho de cara ao voltar pra um método de conta bancária.
watch(
  () => selectedDepositMethod,
  () => {
    showBankAccountConsentError.value = false;
  },
);

// Mesmo agrupamento da tela de resultado (ver v-else-if de
// DepositResultSpei/Oxxo/AstroPay/Worldline/CreditCard mais abaixo no
// template): esses métodos não envolvem "a conta bancária usada no
// depósito" — só os métodos PIX (bucket "default", ex.: pix/triopay/riopag)
// fazem esse match de titularidade. Bug real: sem esse gate, o checkbox de
// consentimento aparecia e bloqueava o botão de depositar mesmo pra quem
// pagava com cartão/spei/oxxo/etc.
const NON_BANK_ACCOUNT_DEPOSIT_METHODS: PaymentsMethodsType[] = [
  'spei',
  'oxxo',
  'astropay',
  'worldline',
  'credit-card',
  'prontopaga',
];

const isBankAccountDepositMethod = computed(
  () => !NON_BANK_ACCOUNT_DEPOSIT_METHODS.includes(selectedDepositMethod),
);

function wipeDepositPaymentResponse() {
  paymentDefaultRes.value.qr_code = '';
  paymentDefaultRes.value.br_code = '';
  paymentDefaultRes.value.clabe = '';
  paymentDefaultRes.value.payment_link = '';
  paymentDefaultRes.value.barcode = '';
  paymentDefaultRes.value.barcode_number = '';
  paymentDefaultRes.value.astropay_response = null;
  paymentDefaultRes.value.value = 0;
  paymentDefaultRes.value.amount = 0;
}

function backToPaymentScreen() {
  wipeDepositPaymentResponse();
  emit('change-screen', 'payment_list');
}

function setDepositAmount(value: number) {
  depositValues.value.amount = String(value);
}

const fullValueDepositAmount = computed(() => {
  return Math.round(parseFloat(depositValues.value.amount || '0') * 100);
});

async function submitDeposit() {
  if (!userHasDocument) {
    await submitDocument().then(async () => {
      await handleSubmitDeposit();
    });
  } else {
    await handleSubmitDeposit();
  }
}

async function initSubmitDeposit() {
  if (isBankAccountDepositMethod.value && !saveBankAccount.value) {
    showBankAccountConsentError.value = true;
    checkboxShakeKey.value++;

    return;
  }

  await submitDeposit();
}

const SEVEN_SECONDS_IN_MS = 7 * 1000;
const isCountdownDone = ref<boolean>(false);

// Bug real: gerar um novo QR não cancelava o polling do QR anterior
// abandonado — os dois loops (handleDeposit recursivo) rodavam em paralelo
// compartilhando isCountdownDone. Se o QR abandonado expirasse de verdade
// depois, seu `else if (status === 'expired')` setava isCountdownDone=true
// e matava o polling do QR atual também. Comparar contra o transaction_id
// "ativo" faz o loop de um QR abandonado desistir sozinho, sem tocar em
// estado compartilhado.
let activeDepositTransactionId = '';

const depositApprovedInfo = ref<{
  amountLabel: string;
  bankAccountLabel: string | null;
  agencyAccountLabel: string | null;
  differentAccount: boolean;
  isNewAccount: boolean;
} | null>(null);

function finishApprovedDeposit() {
  depositApprovedInfo.value = null;
  closeModal();
}

const isDepositDenied = ref(false);

function retryDeniedDeposit() {
  isDepositDenied.value = false;
  backToPaymentScreen();
}

function closeDeniedDeposit() {
  isDepositDenied.value = false;
  closeModal();
}

function onCountdownDone() {
  isCountdownDone.value = true;

  // TODO: Quando expirar o pix, a gente tinha que mostrar uma mensagem mandando gerar um novo pix e parar o handleDeposit
}

async function handleDeposit(transaction_id: string) {
  if (isCountdownDone.value || transaction_id !== activeDepositTransactionId) {
    return;
  }

  if (!transaction_id) {
    return;
  }

  await sleep(SEVEN_SECONDS_IN_MS);

  const { data } = await ApiService.get(`/wallet/charge/${transaction_id}`);

  if (
    modalType.value !== 'deposit' ||
    !isModalOpen.value ||
    transaction_id !== activeDepositTransactionId
  ) {
    return;
  }

  if (data?.status === 'approved') {
    updateWallet();

    const bankAccount = data?.bank_account;

    // hasBankAccount só diz "o jogador tem alguma conta cadastrada" — não
    // que a conta usada NESTE pagamento é diferente da cadastrada. Bug real:
    // sem comparar de fato, o aviso de "conta diferente" aparecia mesmo
    // quando o pagamento veio da própria conta já cadastrada.
    const isRegisteredAccount = bankAccount
      ? userBankAccounts.value.some(
          (account) =>
            account.agency === bankAccount.agency &&
            account.number === bankAccount.number,
        )
      : false;

    depositApprovedInfo.value = {
      amountLabel: showMoneyFormat({
        value: (data.amount ?? 0) / 100,
        withCents: true,
      }),
      bankAccountLabel: bankAccount ? bankAccount.bank_name : null,
      agencyAccountLabel: bankAccount
        ? `${t('bank_account.payer_accounts.agency_account', {
            agency: joinDigit(bankAccount.agency, bankAccount.agency_digit),
            number: joinDigit(bankAccount.number, bankAccount.number_digit),
          })} · ${getBankAccountTypeLabel(bankAccount.type, t)}`
        : null,
      differentAccount:
        !!bankAccount && hasBankAccount.value && !isRegisteredAccount,
      isNewAccount: !!bankAccount && !isRegisteredAccount,
    };
  } else if (data?.status === 'error') {
    // Guia Depósito §4: "error" = recusa por titularidade divergente (conta
    // usada no pagamento pertence a outro CPF) — nenhum valor é debitado.
    isCountdownDone.value = true;
    isDepositDenied.value = true;
  } else if (data?.status === 'expired') {
    isCountdownDone.value = true;

    openToaster({
      message: t('deposit.errors.pix_expired'),
      type: 'error',
    });
  } else {
    await handleDeposit(transaction_id);
  }
}

const limitAlertError = ref<string>('');

async function handleSubmitDeposit() {
  if (isDepositLoading.value) {
    return;
  }

  if (!fullValueDepositAmount.value) {
    openToaster({
      message: t('deposit.errors.insert_value'),
      type: 'error',
    });

    return;
  }

  isDepositLoading.value = true;

  const { isValidationModuleEnabled } = useModulesValidation();

  if (
    isValidationModuleEnabled('deposit') ||
    isValidationModuleEnabled('first_deposit')
  ) {
    const { fetchUserProfile, hasFirstDeposit } = useUserData();
    const { getMinValueToTriggerModule, getValidationModule } =
      useModulesValidation();

    const openVerificationType = ref<Containment.Scope | null>(null);

    await fetchUserProfile({ force: true });

    // O plugin de containment monta a config no boot, quando o perfil do usuário
    // ainda pode não ter chegado. Como a isenção no FTD depende de um campo do perfil
    // (`ftd_validation_exempt`), recalcula o escopo com o perfil recém-buscado acima —
    // senão o modal exigiria verificação de quem está isento.
    $containment.updateConfig({
      first_deposit: getValidationModule('first_deposit'),
    });

    if (!hasFirstDeposit.value && isValidationModuleEnabled('first_deposit')) {
      if (
        fullValueDepositAmount.value >=
        getMinValueToTriggerModule('first_deposit')
      ) {
        await $containment.fetchCurrentPending('first_deposit');

        if ($containment.hasPendencyOn('first_deposit')) {
          openVerificationType.value = 'first_deposit';
        }
      }
    } else if (isValidationModuleEnabled('deposit')) {
      if (
        fullValueDepositAmount.value >= getMinValueToTriggerModule('deposit')
      ) {
        await $containment.fetchCurrentPending('deposit');

        if ($containment.hasPendencyOn('deposit')) {
          openVerificationType.value = 'deposit';
        }
      }
    }

    if (openVerificationType.value) {
      isDepositLoading.value = false;

      await $containment.handle(
        openVerificationType.value,
        async () => {
          isDepositLoading.value = true;

          await submitDepositAPI();
        },
        {
          action: 'modal',
        },
      );

      return;
    }
  }

  await submitDepositAPI();
}

async function submitDepositAPI() {
  // Modal legado (openModal('deposit')): sem chamador conhecido desde o
  // ENG-361, mas ainda montável pelo Global.vue. Com os depósitos desligados no
  // core (ENG-915), não envia: troca pelo modal novo, que mostra o aviso.
  if (isDepositSuspended()) {
    closeModal();
    useGameDepositModal().open();

    return;
  }

  const utm_campaign = utmCampaign;
  const utm_source = utmSource;
  const utm_medium = utmMedium;

  const query_params: Record<string, unknown> = {
    user_id: userId,
    credit_amount: fullValueDepositAmount.value,
    payment_method: selectedDepositMethod,
    currency: userCurrency,
    save_bank_account: saveBankAccount.value,
    ...(utm_campaign ? { utm_campaign } : {}),
    ...(utm_source ? { utm_source } : {}),
    ...(utm_medium ? { utm_medium } : {}),
  };

  if (gaClientId.value) {
    query_params.ga_client_id = gaClientId.value;
  }

  // reset all payment response
  wipeDepositPaymentResponse();

  const realValueFromUser = showMoneyFormat({
    value: fullValueDepositAmount.value / 100,
    withCents: true,
  });

  limitAlertError.value = '';

  try {
    await ApiService.post('/wallet/add-credit', query_params)
      .then((response) => {
        if (response.status.toString().startsWith('20')) {
          const responseData = response.data;
          const depositTransactionId = responseData?.transaction_id;

          if (depositTransactionId) {
            makeDepositEvent(depositTransactionId);
          }

          if (responseData.qr_code) {
            paymentDefaultRes.value.qr_code = responseData.qr_code;
          }
          if (responseData.br_code) {
            paymentDefaultRes.value.br_code = responseData.br_code;
          }
          if (responseData.clabe) {
            paymentDefaultRes.value.clabe = responseData.clabe;
          }
          if (responseData.payment_link) {
            paymentDefaultRes.value.payment_link = responseData.payment_link;
          }
          if (responseData.barcode) {
            paymentDefaultRes.value.barcode = responseData.barcode;
          }
          if (responseData.barcode_number) {
            paymentDefaultRes.value.barcode_number =
              responseData.barcode_number;
          }
          if (responseData.astropay_response) {
            paymentDefaultRes.value.astropay_response =
              responseData.astropay_response;
          }
          if (responseData.checkout_page) {
            paymentDefaultRes.value.checkout_page = responseData.checkout_page;
          }

          paymentDefaultRes.value.value = realValueFromUser;
          paymentDefaultRes.value.amount = realValueFromUser;

          $metrics.push('facebook')?.handle('trackCustom', 'PixGenerated', {
            content_name: 'PIX Gerado',
            currency: useRuntimeConfig().public.brand.currency,
            value: fullValueDepositAmount.value / 100,
          });

          $metrics.push('gtm')?.handle({
            event: 'pix_generated',
            ...gtmUserParams(),
            currency: useRuntimeConfig().public.brand.currency,
            value: fullValueDepositAmount.value / 100,
            transaction_id: responseData.transaction_id,
          });

          $metrics.push('appsflyer')?.handle('af_initiated_checkout', {
            af_price: fullValueDepositAmount.value / 100,
            af_content_type: 'deposit_pix',
            af_order_id: responseData.transaction_id,
          });

          emit('change-screen', 'result_default');

          if (responseData.transaction_id) {
            // Bug real: sem resetar, um QR anterior expirado/abandonado
            // deixava isCountdownDone em `true` pra sempre — o polling deste
            // novo QR nem começava (ver guard no topo de handleDeposit).
            isCountdownDone.value = false;
            activeDepositTransactionId = responseData.transaction_id;
            handleDeposit(responseData.transaction_id);
          }
        } else if (response.status === 429) {
          openChallenge();
        } else {
          const error_msg = response.data?.message
            ? response.data?.message
            : t('deposit.errors.generic');

          openToaster({
            message: error_msg,
            type: 'error',
          });
        }
      })
      .catch(async (error) => {
        if ((isDev() || isStage()) && error.data.exception) {
          // TEST IN DEVELOP MODE
          console.warn('Deposit in development mode');
          console.warn('🚀 ~ query_params:', query_params);

          await sleep(1000);

          // Fake SPEI
          if (selectedDepositMethod === 'spei') {
            paymentDefaultRes.value.clabe = '646180260908280062';
            paymentDefaultRes.value.value = realValueFromUser;
            paymentDefaultRes.value.amount = realValueFromUser;
          } else if (selectedDepositMethod === 'oxxo') {
            paymentDefaultRes.value.barcode =
              'https://quickchart.io/barcode?text=93006052372484&type=code128&width=230&height=50';
            paymentDefaultRes.value.barcode_number = '93006052372484';
            paymentDefaultRes.value.value = realValueFromUser;
            paymentDefaultRes.value.amount = realValueFromUser;
          } else if (selectedDepositMethod === 'credit-card') {
            paymentDefaultRes.value.checkout_page =
              'https://checkout.pagsmile.com?prepay_id=NzQ0blFLZlkyTWt3dURuNmgyMjR1SGNheWFNS3hGUzFYSDZWdUNVbGhGcz0=-e2a6592a';
            paymentDefaultRes.value.value = realValueFromUser;
            paymentDefaultRes.value.amount = realValueFromUser;
          } else {
            // Fake QR Code
            const br_codeLink =
              '00020126890014BR.GOV.BCB.PIX2567api-pix.bancobs2/spi/v2/5c0e34b7-1448-43d4-b9a9-1328e3838b1a520400005303986540530.005802BR5925Pay Brokers Cobranca E Se6014Belo Horizonte61083038040362070503***63040F6C';

            paymentDefaultRes.value.qr_code = '/assets/images/qr-code-dev.png';
            paymentDefaultRes.value.br_code = br_codeLink;
            paymentDefaultRes.value.payment_link = br_codeLink;
            paymentDefaultRes.value.amount = realValueFromUser;
          }

          emit('change-screen', 'result_default');

          return;
        }

        if (error.status === 429) {
          openToaster({
            message: t('deposit.errors.challenge'),
            type: 'error',
          });

          openChallenge();

          return;
        }

        const error_msg = error.data?.message
          ? error.data?.message
          : t('deposit.errors.generic');

        if (error.data?.limit_error) {
          limitAlertError.value = error_msg;

          return;
        }

        openToaster({
          message: error_msg,
          type: 'error',
          time: 10000,
        });
      });
  } catch (err) {
    openToaster({
      message: t('deposit.errors.generic_catch'),
      type: 'error',
    });

    console.error('error sub form:', err);
  } finally {
    await sleep(1000);

    isDepositLoading.value = false;
  }
}

const isDocumentValid = computed<boolean>(() => {
  if (userHasDocument) {
    return true;
  }

  if (userCountry === 'MEX') {
    return !!depositValues.value.doc.docnumber;
  } else if (userCountry === 'BRA') {
    return (
      !!depositValues.value.doc.docnumber &&
      !!depositValues.value.doc.docnumber_valid
    );
  }

  return true;
});

const isSubmitButtonEnabled = computed<boolean>(() => {
  if (!userHasDocument) {
    if (!isDocumentValid.value) {
      return false;
    }
  }

  if (fullValueDepositAmount.value > maxDepositAmount) {
    return false;
  }

  return fullValueDepositAmount.value >= minDepositAmount;
});

const documentCpfRef = ref<InstanceType<typeof DocumentCpf> | null>(null);

const antiRobotToken = computed(
  () => documentCpfRef.value?.antiRobotToken ?? '',
);

function updateDocumentFromComponent(data: UserDocument) {
  depositValues.value.doc.docnumber_valid = data.docnumber_valid || false;
  depositValues.value.doc.docnumber = data.docnumber || '';
  depositValues.value.doc.full_name = data.name || '';
  depositValues.value.doc.birth_date = data.birth_date || '';
}

async function submitDocument() {
  if (!depositValues.value.doc.docnumber) {
    openToaster({
      message: t('deposit.errors.insert_cpf'),
      type: 'error',
    });

    return;
  }

  isDepositLoading.value = true;

  const docNumber = computed(() => {
    return depositValues.value.doc.docnumber
      .replaceAll('-', '')
      .replaceAll('.', '');
  });

  const data = {
    number: docNumber.value,
    captcha_token: antiRobotToken.value,
  };

  if (userCountry === 'MEX') {
    await updateUserDocumentCURP(data)
      .then(async () => {
        await fetchUserProfile();
      })
      .catch((err) => {
        console.debug('🚀 err:', err);

        isDepositLoading.value = false;

        openToaster({
          message: err.message,
          type: 'error',
        });
      });
  } else {
    await updateUserDocumentCPF(data)
      .then(async () => {
        await fetchUserProfile();
      })
      .catch((err) => {
        console.debug('🚀 err:', err);

        isDepositLoading.value = false;

        openToaster({
          message: err.message,
          type: 'error',
        });
      });
  }
}

const depositError = computed<string>(() => {
  if (fullValueDepositAmount.value < minDepositAmount) {
    return `${t('deposit.errors.min_deposit_value')}
    ${showMoneyFormat({ value: minDepositAmount / 100, withCents: true })}`;
  }

  if (fullValueDepositAmount.value > maxDepositAmount) {
    return `${t('deposit.errors.max_deposit_value')}
    ${showMoneyFormat({ value: maxDepositAmount / 100, withCents: true })}`;
  }

  return '';
});

const isAmountDirty = ref<boolean>(false);

watchOnce(
  () => depositValues.value.amount,
  () => {
    isAmountDirty.value = true;
  },
);

const isDepositErrorsVisible = computed<boolean>(() => {
  if (!depositError.value) {
    return false;
  }

  return isAmountDirty.value;
});
</script>

<template>
  <ClientOnly>
    <DepositResultDenied
      v-if="isDepositDenied"
      @close="closeDeniedDeposit"
      @retry="retryDeniedDeposit"
    />

    <DepositResultApproved
      v-else-if="depositApprovedInfo"
      :agency-account-label="depositApprovedInfo.agencyAccountLabel"
      :amount-label="depositApprovedInfo.amountLabel"
      :bank-account-label="depositApprovedInfo.bankAccountLabel"
      :different-account="depositApprovedInfo.differentAccount"
      :is-new-account="depositApprovedInfo.isNewAccount"
      @toggle-modal-inner="finishApprovedDeposit"
    />

    <div
      v-else-if="!isResultScreen"
      class="w-full"
    >
      <form
        class="w-full md:max-w-[450px] flex flex-col gap-2 mx-auto relative"
        @submit.prevent="initSubmitDeposit"
      >
        <BaseLoading
          class="!bg-auth-bg-primary z-20"
          :icon-loader="true"
          :show="showLoading"
        />

        <div
          v-if="!!limitAlertError"
          class="alert alert-error"
          v-html="limitAlertError"
        />

        <div
          v-if="activeFirstDepositMessage"
          class="alert alert-success mb-2 font-medium flex justify-between items-center gap-4 text-left px-4"
        >
          {{ firstDepositMessage }}
          <DepositCountdown />
        </div>
        <div :class="[$style.depositRow, $style.depositInfosRow]">
          <div :class="$style.item">
            <label
              :class="$style.labelInput"
              for="method"
            >
              {{ $t('deposit.general.payment_method') }}
            </label>
            <BaseFormInput
              id="selectedDepositMethod"
              autocomplete="off"
              class="px-3"
              :compact="true"
              disabled
              :initial-value="depositMethodName"
              name="selectedDepositMethod"
              :readonly="true"
              type="text"
            />
          </div>
          <div :class="$style.item">
            <label
              :class="$style.labelInput"
              for="method"
            >
              {{ $t('deposit.general.min_deposit_value') }}
            </label>

            <BaseFormInput
              id="minDepositAmount"
              autocomplete="off"
              class="px-3"
              :compact="true"
              disabled
              :initial-value="
                showMoneyFormat({
                  value: minDepositAmount / 100,
                  withCents: false,
                })
              "
              name="minDepositAmount"
              :readonly="true"
              type="text"
            />
          </div>
        </div>

        <div
          ref="depositRow"
          :class="$style.depositRow"
        >
          <label
            :class="$style.labelInput"
            for="inputCurrency"
          >
            <span>{{ $t('deposit.deposit_amount') }}</span>
          </label>
        </div>

        <BaseFormInputCurrencyV2
          id="inputCurrency"
          v-model="depositValues.amount"
          :initial-value="depositValues.amount"
          :show-currency="true"
          size="lg"
          :user-country="userCountry"
          :user-currency="userCurrency"
          :user-language="userLanguage"
          :with-cents="true"
        />

        <div
          v-if="isDepositErrorsVisible"
          class="alert alert-error font-medium"
        >
          {{ depositError }}
        </div>

        <DepositAmountShortcuts
          :amount-value="fullValueDepositAmount"
          :user-currency="userCurrency"
          :user-language="userLanguage"
          @set-amount-function="setDepositAmount"
        />

        <DepositDocumentCurp
          v-if="userCountry === 'MEX'"
          v-model="depositValues.doc.docnumber"
          :user-has-document="userHasDocument"
        />

        <DepositDocumentCpf
          v-else-if="userCountry === 'BRA'"
          ref="documentCpfRef"
          :user-has-document="userHasDocument"
          @update-user-document-cpf="updateDocumentFromComponent"
        />

        <label
          v-if="isBankAccountDepositMethod"
          :key="checkboxShakeKey"
          :class="[
            $style.checkboxRow,
            { [$style.checkboxRowError]: showBankAccountConsentError },
          ]"
          for="save-bank-account"
        >
          <span :class="$style.checkboxBoxWrap">
            <input
              id="save-bank-account"
              v-model="saveBankAccount"
              :aria-invalid="showBankAccountConsentError"
              :class="$style.checkboxInput"
              type="checkbox"
            />
            <span
              aria-hidden="true"
              :class="[
                $style.checkboxBox,
                {
                  [$style.checked]: saveBankAccount,
                  [$style.checkboxBoxError]: showBankAccountConsentError,
                },
              ]"
            >
              <svg
                v-if="saveBankAccount"
                class="stroke-bg-secondary"
                fill="none"
                height="12"
                viewBox="0 0 16 16"
                width="12"
              >
                <path
                  d="M3 8.5l3.2 3.2L13 4.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                />
              </svg>
            </span>
          </span>
          <span
            :class="[
              $style.checkboxText,
              { [$style.checkboxTextError]: showBankAccountConsentError },
            ]"
          >
            {{
              hasBankAccount
                ? $t('deposit.general.bank_account_consent_different_account')
                : $t('deposit.general.bank_account_consent')
            }}
            <span
              v-if="showBankAccountConsentError"
              :aria-label="$t('deposit.general.bank_account_consent_required')"
              :class="$style.checkboxAsterisk"
              role="alert"
            >
              *
            </span>
          </span>
        </label>

        <button
          :class="$style.btnSubmit"
          :disabled="!isSubmitButtonEnabled || isDepositLoading"
          tabindex="1"
          type="submit"
        >
          {{ $t('deposit.general.button_deposit') }}
        </button>
      </form>
    </div>

    <DepositResultSpei
      v-else-if="selectedDepositMethod === 'spei'"
      :deposit="paymentDefaultRes"
      @toggle-modal-inner="backToPaymentScreen"
    />

    <DepositResultOxxo
      v-else-if="selectedDepositMethod === 'oxxo'"
      :deposit="paymentDefaultRes"
      @toggle-modal-inner="backToPaymentScreen"
    />

    <DepositResultAstroPay
      v-else-if="selectedDepositMethod === 'astropay'"
      :deposit="paymentDefaultRes"
      @toggle-modal-inner="backToPaymentScreen"
    />

    <DepositResultWorldline
      v-else-if="selectedDepositMethod === 'worldline'"
      :deposit="paymentDefaultRes"
      @toggle-modal-inner="backToPaymentScreen"
    />

    <DepositResultCreditCard
      v-else-if="
        selectedDepositMethod === 'credit-card' ||
        selectedDepositMethod === 'prontopaga'
      "
      :deposit="paymentDefaultRes"
      @toggle-modal-inner="backToPaymentScreen"
    />

    <DepositResultDefault
      v-else
      :deposit="paymentDefaultRes"
      @countdown-done="onCountdownDone"
      @toggle-modal-inner="backToPaymentScreen"
    />
  </ClientOnly>
</template>

<style module lang="postcss">
.depositRow {
  @apply flex flex-wrap gap-2 items-baseline justify-between;

  .labelInput {
    @apply text-auth-texts text-xs font-semibold sm:text-sm mb-0;
  }

  &.depositInfosRow {
    @apply mb-4 flex-nowrap gap-4;

    .item {
      @apply flex-1;

      .labelInput {
        @apply text-auth-texts/50 font-medium text-xs;
      }

      input {
        @apply uppercase;
      }
    }
  }
}

.btnSubmit {
  @apply flex justify-center items-center rounded-md font-medium text-lg uppercase bg-auth-button-deposit-bg text-auth-button-deposit-text w-full px-3 py-3 cursor-pointer;

  &[disabled]:not([disabled='false']) {
    @apply opacity-40 cursor-not-allowed;
  }
}

.checkboxRow {
  @apply flex items-start gap-2.5 mb-4 py-3 px-3.5 rounded-lg bg-primary/[0.06] border border-solid border-primary/20 cursor-pointer;

  /* clicou em Depositar sem marcar — obrigatório, mas não bloqueia mais o
     botão (isSubmitButtonEnabled); avisa aqui em vez de só desabilitar sem
     explicação. */
  &.checkboxRowError {
    @apply bg-error/[0.06] border-error/40;
    animation: checkbox-shake 0.4s ease-in-out;
  }
}

.checkboxBoxWrap {
  @apply relative shrink-0 w-5 h-5 mt-px;
}

.checkboxInput {
  @apply absolute inset-0 w-5 h-5 opacity-0 cursor-pointer;
}

.checkboxBox {
  @apply absolute inset-0 rounded-[5px] border-[1.5px] border-solid border-texts/40 bg-transparent flex items-center justify-center transition-colors duration-150;

  &.checked {
    @apply bg-primary border-primary;
  }

  &.checkboxBoxError {
    @apply border-error;
  }
}

.checkboxText {
  @apply text-texts/85 text-xs leading-[1.5] text-left;

  &.checkboxTextError {
    @apply text-error;
  }
}

.checkboxAsterisk {
  @apply text-error font-bold;
}

@keyframes checkbox-shake {
  10%,
  90% {
    transform: translate(-1px, 0.5px);
  }

  20%,
  80% {
    transform: translate(2px, -0.5px);
  }

  30%,
  50%,
  70% {
    transform: translate(-4px, 0.3px);
  }

  40%,
  60% {
    transform: translate(4px, -0.5px);
  }
}
</style>
